import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import CreateCustomThingToDo from '../Create/CreateCustomThingToDo'
import SearchExperience from '../Search/SearchExperience'

import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'

type Props = {
  tripId: string
  defaultDate?: string
  sectionId?: string
  rankSpec?: CreateTripItemRankSpec
}

function SelectThingsToDoType(props: Props) {
  const { tripId, sectionId, defaultDate, rankSpec } = props

  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const handleSearchThingsTodoClick = useCallback(() => {
    openModal(SearchExperience, { tripId, sectionId })
  }, [openModal, sectionId, tripId])

  const handleAddCustomThingTodo = useCallback(() => {
    openModal(CreateCustomThingToDo, {
      tripId,
      defaultDate,
      rankSpec,
    })
  }, [defaultDate, openModal, tripId, rankSpec])

  const searchTitle = intl.formatMessage({
    id: 'fYLqzCVb2N_a61ushjds',
    defaultMessage: 'Search Luxury Escape Offers',
    description: 'List item title for searching things to do offers in trip planner',
  })
  const searchSubtitle = intl.formatMessage({
    id: 'zZlHL8miAIl6szt5eNVE',
    defaultMessage: 'Need inspiration? Search our exclusive Luxury Escapes offers',
    description: 'List item subtitle encouraging search of things to do offers in trip planner',
  })
  const customTitle = intl.formatMessage({
    id: 'YHEwS09jnA-VNLwUsbjC',
    defaultMessage: 'Add your own',
    description: 'List item title for adding a custom thing to do in trip planner',
  })
  const customSubtitle = intl.formatMessage({
    id: 'cIwIbWkz-XaXeB98Rshj',
    defaultMessage: 'Enter details of your own booking',
    description: 'List item subtitle for adding custom thing to do booking details in trip planner',
  })

  const listItemsData = useMemo(
    () => [
      {
        startIcon: <LineSearchIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'searchThingsToDo',
        title: searchTitle,
        subtitle: searchSubtitle,
        onClick: handleSearchThingsTodoClick,
      },
      {
        startIcon: <LineEditAltIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'addCustomThingToDo',
        title: customTitle,
        subtitle: customSubtitle,
        onClick: handleAddCustomThingTodo,
      },
    ],
    [handleAddCustomThingTodo, handleSearchThingsTodoClick, searchTitle, searchSubtitle, customTitle, customSubtitle],
  )

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'IXh_LwgCAx11pj3hM-En',
          defaultMessage: 'Place',
          description: 'Modal title for things to do type selection in trip planner',
        })}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {listItemsData.map((item) => (
            <ClickableListItem
              key={item.key}
              startIcon={item.startIcon}
              endIcon={item.endIcon}
              title={item.title}
              subtitle={item.subtitle}
              onClick={item.onClick}
            />
          ))}
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SelectThingsToDoType
